// Custom Element base
// TODO: Add observed attributes helper
class ElementBase extends HTMLElement {
  constructor() {
    super();
    this.listeners = new Map();
  }

  connectedCallback() {
    this.log('Connecting');

    this.init();
  }

  disconnectedCallback() {
    this.log('Disconnecting');

    this.destroy();
  }

  // TODO: FOR ALL CALLBACKS - Add bind here instead?
  addEventListeners() {
    this.log('Adding event listeners');
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.addEventListener(event, callback.bind(this));
      }
    }
  }

  removeEventListeners() {
    this.log('Removing event listeners');
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.removeEventListener(event, callback.bind(this));
      }
    }
  }

  addListener(target, event, callback, bound = false) {
    const existingListeners = this.listeners.get(target);
    const listeners = {
      ...existingListeners,
      [event]: bound ? callback : callback.bind(this),
    };

    this.listeners.set(target, listeners);
  }

  addResizeListener(target, callback) {
    callback = debounce(callback.bind(this), RESIZE_DEBOUNCE_TIMER);

    this.addListener(target, 'resize', callback, true);
  }

  init() {
    this.log('Initializing');

    this.options = this.getOptions();
    this.addEventListeners();
  }

  destroy() {
    this.removeEventListeners();
  }

  defaults() {
    return {};
  }

  parseOption(option) {
    option = camelToKebab(option);

    if (!this.hasAttribute(option)) return null;
    const value = this.getAttribute(option);

    return parseAttribute(value);
  }

  getOptions() {
    const defaults = this.defaults();
    const options = {};
    for (const [prop, defaultValue] of Object.entries(defaults)) {
      options[prop] = this.parseOption(prop) ?? defaultValue;
    }

    return options;
  }

  log() {
    console.debug(`${this.constructor.name}:`, ...arguments);
  }

  error() {
    console.error(`${this.constructor.name} Error:`, ...arguments);
  }
}

class ObservedElement extends ElementBase {
  constructor() {
    super();

    this.observerList = new Map();
    this.observers = new Map();
  }

  getObserver(type, target) {
    const observers = this.observers.get(type);

    if (!observers) {
      this.error(`Failed to get observer, no observer of type ${type} found`);
      return false;
    }

    const observer = observers.get(target);

    if (!observer) {
      this.error(`Failed to get ${type} observer, no observer found for target`, target);
      return false;
    }

    return observer;
  }

  addObserver(target, type, data) {
    if (!data.callback) {
      this.error(`Failed to add ${type} observer, no callback provided`);
      return;
    }

    data.callback = data.callback.bind(this);

    const existingObservers = this.observerList.get(type) || new Map();
    const observers = [...existingObservers, [target, data]];

    this.observerList.set(type, observers);
  }

  initObservers() {
    this.log('Initializing observers');
    for (const [type, observers] of this.observerList) {
      for (const [target, data] of observers) {
        this.initObserver(type, target, data);
      }
    }
  }

  initObserver(type, target, data) {
    let observer = null;

    switch (type) {
      case 'mutation':
        observer = this.initMutationObserver(target, data);
        break;
      case 'intersection':
        observer = this.initIntersectionObserver(target, data);
        break;
      case 'resize':
        observer = this.initResizeObserver(target, data);
        break;
    }

    if (!observer) {
      this.error(`Failed to initialize observer, the provided type "${type}" is not supported`);
      return;
    }

    const existingObservers = this.observers.get(type) || new Map();
    const observers = [...existingObservers, [target, observer]];

    this.observers.set(type, observers);
  }

  addMutationObserver(target, callback, { mutationType = 'childList', options = {} } = {}) {
    this.addObserver(target, 'mutation', {
      callback,
      mutationType,
      options: {
        ...options,
        [mutationType]: true,
      },
    });
  }

  initMutationObserver(target, { mutationType, callback, options } = {}) {
    const observer = new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        if (mutation.type === mutationType) {
          callback(mutation, observer);
        }
      }
    });

    observer.observe(target, options);

    return observer;
  }

  addIntersectionObserver(
    target,
    callback,
    { onIntersect = true, root = null, rootMargin = '0px 0px 0px 0px', threshold = 0.0 } = {}
  ) {
    this.addObserver(target, 'intersection', {
      callback,
      onIntersect,
      options: { root, rootMargin, threshold },
    });
  }

  initIntersectionObserver(target, { callback, onIntersect, options } = {}) {
    const observer = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          callback(entry, observer);
        } else if (!onIntersect) {
          callback(entry, observer);
        }
      }
    }, options);

    observer.observe(target);

    return observer;
  }

  addResizeObserver(target, callback, { debounced = true, options } = {}) {
    this.addObserver(target, 'resize', { callback, debounced, options });
  }

  initResizeObserver(target, { callback, debounced, options } = {}) {
    const resize = (entries, observer) => {
      for (const entry of entries) {
        callback(entry, observer);
      }
    };

    const observer = new ResizeObserver(
      debounced ? debounce(resize, RESIZE_DEBOUNCE_TIMER) : resize
    );

    observer.observe(target, options);

    return observer;
  }

  disconnectObservers() {
    this.log('Disconnecting observers');
    for (const [type, observers] of this.observers) {
      for (const [target, observer] of observers) {
        observer.disconnect();
      }
    }
  }

  init() {
    super.init();
    this.initObservers();
  }

  destroy(disconnectObservers = true) {
    super.destroy();

    if (disconnectObservers) {
      this.disconnectObservers();
    }
  }
}